// A tiny module-level bus for the 3D dice overlay. A roll site calls `throwDice(...)` with the dice it ALREADY rolled
// (each physical die + the value it landed on); the <DiceLayer> mounted on the current surface animates them and fades.
//
// ⚠ COSMETIC ONLY. The RNG, the result, the feed and Apply-to-HP all live at the roll site and are untouched — the 3D
// layer never decides an outcome, it only shows the one already rolled.
//
// CROSS-SCREEN (opt-in, default off). A roll always animates on the roller's OWN screen. Beyond that, a "who sees this"
// choice — Private (default) · the whole party · one particular player — decides whether the throw is ALSO broadcast to
// the other people in the CAMPAIGN (use-dice-channel.ts carries it over a campaign-wide Supabase broadcast channel, so
// it reaches a player on ANY screen, and sends from the GM on any screen). Targeting a player delivers only to them.

export type DiceDest =
  | { mode: "mine" }
  | { mode: "party" }
  | { mode: "player"; to: string; name: string };   // `to` is the recipient's user id

export type DiceTarget = { audience: "party" } | { audience: "player"; to: string };

export type DiceThrow = {
  /** Each physical die and the value it rolled — the animation lands each die on this face. */
  dice: { sides: number; value: number }[];
  mod: number;
  total: number;
  title: string;
  tone: "crit" | "fumble" | null;
  /** The roller's dice-colour id, stamped when a throw is shared so the far screen shows their colour (dice-style.ts). */
  styleId?: string;
  /** Set on an INCOMING shared throw, so the layer shows the result caption (you didn't roll it yourself). */
  remote?: boolean;
  /** A roll about something hidden from players (a hidden creature) — never leaves this screen, whatever "who sees this" says. */
  secret?: boolean;
};

type Handler = (t: DiceThrow) => void;
let handler: Handler | null = null;

/** The mounted DiceLayer registers itself here (only one surface is live at a time). Pass null to unregister. */
export function onDiceThrow(h: Handler | null): void { handler = h; }

/** Animate on THIS screen only — used both by the local roll and by an incoming shared roll (never re-broadcasts). */
export function animateDice(t: DiceThrow): void {
  try { handler?.(t); } catch { /* the animation must never break a roll */ }
}

// ── Sharing ────────────────────────────────────────────────────────────────────────────────────────

type Broadcaster = (t: DiceThrow, target: DiceTarget) => void;
let broadcaster: Broadcaster | null = null;

/** use-dice-channel.ts registers the sender while it is mounted. Returns an unsubscribe. */
export function onDiceShareBroadcast(b: Broadcaster | null): () => void {
  broadcaster = b;
  return () => { if (broadcaster === b) broadcaster = null; };
}

const MODE_KEY = "compendium.diceShare";
function readDest(): DiceDest {
  // Only Private/Party persist; a one-player target is for the moment, not remembered across reloads.
  try { return localStorage.getItem(MODE_KEY) === "party" ? { mode: "party" } : { mode: "mine" }; } catch { return { mode: "mine" }; }
}
let dest: DiceDest = readDest();
const listeners = new Set<(d: DiceDest) => void>();

export function getDiceDest(): DiceDest { return dest; }
export function setDiceDest(d: DiceDest): void {
  dest = d;
  try { if (d.mode !== "player") localStorage.setItem(MODE_KEY, d.mode); } catch { /* storage blocked — still applies this session */ }
  listeners.forEach((f) => f(d));
}
export function onDiceDestChange(f: (d: DiceDest) => void): () => void { listeners.add(f); return () => listeners.delete(f); }

/**
 * A roll site announces a throw: it animates here, and — per "who sees this" — is shared to the party or one player. A
 * `secret` throw (a hidden creature's roll) is never shared, whatever the choice, so a hidden monster's dice can't leak.
 */
export function throwDice(t: DiceThrow): void {
  animateDice(t);
  if (t.secret) return;
  if (dest.mode === "party") { try { broadcaster?.(t, { audience: "party" }); } catch { /* sharing must never break a roll */ } }
  else if (dest.mode === "player") { const to = dest.to; try { broadcaster?.(t, { audience: "player", to }); } catch { /* never break a roll */ } }
}
